import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Drawer, IconButton, InputAdornment,
  MenuItem, Paper, TextField, Tooltip, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import InventoryOutlinedIcon from "@mui/icons-material/InventoryOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useNotifier } from "../../../Core/Notifier";
import { api, T, inputSx, fmtCAD, PRICING_TYPES, PRICING_LABELS } from "../ServicesManager/api";
import { uploadImage } from "../../../Components/UploadFile/upload";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";

/* ═══════════════════════════════════════════════════════════════════════════
   ADD/EDIT VENDOR PRICE FORM (inline in drawer)
   ═══════════════════════════════════════════════════════════════════════════ */
const VendorPriceForm: React.FC<{
  itemId: number; vendors: any[]; assignedVendorIds: number[];
  supportedPricingTypes?: string[];
  editPrice?: any | null;
  onSaved: () => void; onCancel: () => void;
}> = ({ itemId, vendors, assignedVendorIds, supportedPricingTypes, editPrice, onSaved, onCancel }) => {
  const { showMessage, showErrorMessage } = useNotifier();
  const [vendorId, setVendorId] = useState(editPrice?.vendor_id?.toString() || "");
  const [pricingType, setPricingType] = useState(editPrice?.pricing_type || "per_event");
  const [vendorPrice, setVendorPrice] = useState(editPrice?.vendor_price?.toString() || "0");
  const [dccPrice, setDccPrice] = useState(editPrice?.dcc_price?.toString() || "0");
  const [minHours, setMinHours] = useState(editPrice?.min_hours?.toString() || "");
  const [unitLabel, setUnitLabel] = useState(editPrice?.unit_label || "");
  const [isDefault, setIsDefault] = useState(editPrice?.is_default ?? false);
  const [imageUrl, setImageUrl] = useState(editPrice?.image_url || "");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const imgInputRef = React.useRef<HTMLInputElement>(null);

  const API_URL = process.env.REACT_APP_API_URL || "";

  // Filter pricing types by item's supported types
  const availableTypes = supportedPricingTypes && supportedPricingTypes.length > 0
    ? PRICING_TYPES.filter((pt) => supportedPricingTypes.includes(pt.value))
    : PRICING_TYPES;

  const handleImageUpload = async (file: File) => {
    if (!file.type.startsWith("image/")) return;
    if (file.size > 2 * 1024 * 1024) return;
    setUploading(true);
    try {
      const path = await uploadImage(file);
      if (path) setImageUrl(`${API_URL}/../${path}`);
    } catch { /* ignore */ }
    finally { setUploading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    const res = await api.post("/banquet/service-tree/item-vendor", {
      id: editPrice?.id || undefined,
      item_id: itemId,
      vendor_id: Number(vendorId),
      pricing_type: pricingType,
      vendor_price: Number(vendorPrice),
      dcc_price: Number(dccPrice),
      min_hours: minHours ? Number(minHours) : null,
      unit_label: unitLabel || null,
      is_default: isDefault,
      image_url: imageUrl || null,
    });
    if (res.code === 200) { showMessage(res.message); onSaved(); }
    else showErrorMessage(res.message);
    setSaving(false);
  };

  return (
    <Paper elevation={0} sx={{ p: 2, borderRadius: "12px", border: `1.5px solid ${T.gold}40`, bgcolor: `${T.gold}06`, mb: 1.5 }}>
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: T.gold, mb: 1.5, textTransform: "uppercase" }}>
        {editPrice ? "Edit Vendor Price" : "Assign Vendor"}
      </Typography>

      {!editPrice && (
        <TextField select label="Vendor" size="small" value={vendorId} onChange={(e) => setVendorId(e.target.value)} fullWidth sx={{ ...inputSx, mb: 1.5 }}>
          <MenuItem value="" disabled>Select vendor...</MenuItem>
          {vendors.filter((v) => !assignedVendorIds.includes(v.id) || v.id === editPrice?.vendor_id).map((v: any) => (
            <MenuItem key={v.id} value={v.id}>{v.name}{v.company_name ? ` (${v.company_name})` : ""}</MenuItem>
          ))}
        </TextField>
      )}

      <TextField select label="Pricing Type" size="small" value={pricingType} onChange={(e) => setPricingType(e.target.value)} fullWidth sx={{ ...inputSx, mb: 1.5 }}>
        {availableTypes.map((pt) => <MenuItem key={pt.value} value={pt.value}>{pt.label}</MenuItem>)}
      </TextField>

      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, mb: 1.5 }}>
        <TextField label="Vendor Price" type="number" size="small" value={vendorPrice} onChange={(e) => setVendorPrice(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} sx={inputSx} />
        <TextField label="DCC Price" type="number" size="small" value={dccPrice} onChange={(e) => setDccPrice(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} sx={inputSx} />
      </Box>

      {/* Conditional fields based on pricing type */}
      {pricingType === "per_hour" && (
        <TextField label="Min Hours" type="number" size="small" value={minHours} onChange={(e) => setMinHours(e.target.value)} fullWidth
          inputProps={{ min: 0, step: 0.5 }} sx={{ ...inputSx, mb: 1.5 }} />
      )}
      {pricingType === "per_unit" && (
        <TextField label="Unit Label (e.g. chairs, tables)" size="small" value={unitLabel} onChange={(e) => setUnitLabel(e.target.value)} fullWidth
          sx={{ ...inputSx, mb: 1.5 }} />
      )}

      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
        <Button size="small"
          startIcon={isDefault ? <StarIcon sx={{ fontSize: 13 }} /> : <StarBorderIcon sx={{ fontSize: 13 }} />}
          onClick={() => setIsDefault(!isDefault)}
          sx={{ textTransform: "none", fontSize: 11, color: isDefault ? T.gold : T.slateLt }}>
          {isDefault ? "Default" : "Not Default"}
        </Button>
      </Box>

      {/* Vendor-specific image */}
      <Box sx={{ mb: 1.5 }}>
        <Typography sx={{ fontSize: 10, fontWeight: 600, color: T.slateLt, mb: 0.5, textTransform: "uppercase" }}>Vendor Image</Typography>
        <input ref={imgInputRef} type="file" accept="image/*" hidden onChange={(e) => { if (e.target.files?.[0]) handleImageUpload(e.target.files[0]); }} />
        {imageUrl ? (
          <Box sx={{ position: "relative", display: "inline-block" }}>
            <Box component="img" src={imageUrl} alt="Vendor item" sx={{ width: 100, height: 70, objectFit: "cover", borderRadius: "8px", border: `1px solid ${T.line}` }} />
            <IconButton size="small" onClick={() => setImageUrl("")}
              sx={{ position: "absolute", top: -8, right: -8, bgcolor: T.white, border: `1px solid ${T.line}`, width: 20, height: 20, "&:hover": { bgcolor: "#fee" } }}>
              <CloseIcon sx={{ fontSize: 12 }} />
            </IconButton>
          </Box>
        ) : (
          <Button size="small" variant="outlined" disabled={uploading}
            startIcon={uploading ? <CircularProgress size={12} /> : <AddPhotoAlternateOutlinedIcon sx={{ fontSize: 14 }} />}
            onClick={() => imgInputRef.current?.click()}
            sx={{ textTransform: "none", fontSize: 11, color: T.slateLt, borderColor: T.line, borderStyle: "dashed", borderRadius: "8px", px: 2, py: 0.8 }}>
            {uploading ? "Uploading..." : "Upload Image"}
          </Button>
        )}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
        <Button size="small" onClick={onCancel} sx={{ textTransform: "none", fontSize: 12, color: T.slate }}>Cancel</Button>
        <Button size="small" variant="contained" onClick={handleSave} disabled={saving || (!editPrice && !vendorId)}
          startIcon={saving ? <CircularProgress size={12} color="inherit" /> : <SaveAltIcon sx={{ fontSize: 14 }} />}
          sx={{ textTransform: "none", fontSize: 12, fontWeight: 600, bgcolor: T.navy, borderRadius: "8px", px: 2, "&:hover": { bgcolor: "#1a3550" } }}>
          {saving ? "..." : "Save"}
        </Button>
      </Box>
    </Paper>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   ITEM PROFILE DRAWER
   ═══════════════════════════════════════════════════════════════════════════ */
const ItemProfileDrawer: React.FC<{
  itemId: number | null;
  onClose: () => void;
  onRefresh: () => void;
}> = ({ itemId, onClose, onRefresh }) => {
  const { showMessage, showErrorMessage } = useNotifier();
  const [item, setItem] = useState<any>(null);
  const [vendors, setVendors] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // Add/edit vendor price
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingPrice, setEditingPrice] = useState<any | null>(null);

  // Delete vendor price
  const [deleteVP, setDeleteVP] = useState<{ id: number; vendorName: string } | null>(null);
  const [deletingVP, setDeletingVP] = useState(false);

  const fetchItem = useCallback(async () => {
    if (!itemId) return;
    setLoading(true);
    const [itemRes, vRes] = await Promise.all([
      api.get(`/banquet/items-list/${itemId}`),
      api.get("/banquet/vendors"),
    ]);
    if (itemRes.code === 200) setItem(itemRes.data);
    if (vRes.code === 200) setVendors(vRes.data ?? []);
    setLoading(false);
    setShowAddForm(false);
    setEditingPrice(null);
  }, [itemId]);

  useEffect(() => { if (itemId) fetchItem(); }, [itemId, fetchItem]);

  const handleSetDefault = async (vendorPriceId: number) => {
    if (!itemId) return;
    const res = await api.post(`/banquet/service-tree/item/${itemId}/set-default`, { vendor_price_id: vendorPriceId });
    if (res.code === 200) { showMessage("Default vendor set"); fetchItem(); onRefresh(); }
    else showErrorMessage(res.message);
  };

  const handleDeleteVP = async () => {
    if (!deleteVP) return;
    setDeletingVP(true);
    const res = await api.del(`/banquet/service-tree/item-vendor/${deleteVP.id}`);
    if (res.code === 200) { showMessage("Vendor removed"); fetchItem(); onRefresh(); }
    else showErrorMessage(res.message);
    setDeletingVP(false);
    setDeleteVP(null);
  };

  const handleVendorSaved = () => {
    fetchItem();
    onRefresh();
    setShowAddForm(false);
    setEditingPrice(null);
  };

  const assignedVendorIds = (item?.vendor_prices ?? []).map((vp: any) => vp.vendor_id);

  return (
    <>
      <Drawer
        anchor="right" open={!!itemId} onClose={onClose}
        PaperProps={{ sx: { width: { xs: "100%", sm: 480 }, bgcolor: T.cream } }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}><CircularProgress sx={{ color: T.gold }} /></Box>
        ) : !item ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography sx={{ color: T.slateLt }}>Item not found.</Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
            {/* ── Header ── */}
            <Box sx={{ px: 3, py: 2, background: `linear-gradient(135deg, ${T.navy} 0%, #1a3550 100%)`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                <Box sx={{ width: 36, height: 36, borderRadius: "10px", bgcolor: "rgba(201,169,110,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <InventoryOutlinedIcon sx={{ fontSize: 18, color: T.gold }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 16, fontWeight: 700, color: T.white }}>{item.name}</Typography>
                  <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.5)" }}>
                    {item.service?.name}{item.category ? ` / ${item.category.name}` : ""}{item.subcategory ? ` / ${item.subcategory.name}` : ""}
                  </Typography>
                </Box>
              </Box>
              <IconButton onClick={onClose} sx={{ color: "rgba(255,255,255,0.6)" }}><CloseIcon sx={{ fontSize: 20 }} /></IconButton>
            </Box>

            {/* ── Content ── */}
            <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
              {/* Item Details */}
              <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: "12px", border: `1px solid ${T.line}` }}>
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: T.slateLt, mb: 1.5, textTransform: "uppercase" }}>Item Details</Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
                  <Box>
                    <Typography sx={{ fontSize: 10, color: T.slateLt }}>Service</Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.3 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: item.service?.color ?? T.slateLt }} />
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.navy }}>{item.service?.name ?? "—"}</Typography>
                    </Box>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 10, color: T.slateLt }}>Category</Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.navy, mt: 0.3 }}>{item.category?.name ?? "—"}</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 10, color: T.slateLt }}>Subcategory</Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.navy, mt: 0.3 }}>{item.subcategory?.name ?? "—"}</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 10, color: T.slateLt }}>Unit</Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.navy, mt: 0.3 }}>{item.unit ?? "—"}</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 10, color: T.slateLt }}>Default Price</Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: item.default_price != null ? T.greenTx : T.slateLt, mt: 0.3 }}>
                      {item.default_price != null ? fmtCAD(Number(item.default_price)) : "—"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 10, color: T.slateLt }}>Status</Typography>
                    <Chip label={item.status} size="small" sx={{ height: 18, fontSize: 10, fontWeight: 600, mt: 0.3, bgcolor: item.status === "active" ? T.greenBg : T.redBg, color: item.status === "active" ? T.greenTx : T.redTx }} />
                  </Box>
                </Box>
                {item.supported_pricing_types && item.supported_pricing_types.length > 0 && (
                  <Box sx={{ mt: 1.5 }}>
                    <Typography sx={{ fontSize: 10, color: T.slateLt, mb: 0.5 }}>Supported Pricing Types</Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {item.supported_pricing_types.map((pt: string) => (
                        <Chip key={pt} label={PRICING_LABELS[pt] ?? pt} size="small"
                          sx={{ height: 20, fontSize: 10, fontWeight: 600, bgcolor: `${T.gold}12`, color: T.gold }} />
                      ))}
                    </Box>
                  </Box>
                )}
                {item.description && (
                  <Box sx={{ mt: 1.5 }}>
                    <Typography sx={{ fontSize: 10, color: T.slateLt }}>Description</Typography>
                    <Typography sx={{ fontSize: 12, color: T.slate, mt: 0.3 }}>{item.description}</Typography>
                  </Box>
                )}
              </Paper>

              {/* Vendor Pricing Section */}
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.navy }}>
                  Vendor Pricing ({(item.vendor_prices ?? []).length})
                </Typography>
                {!showAddForm && !editingPrice && (
                  <Button size="small" startIcon={<AddIcon sx={{ fontSize: 14 }} />} onClick={() => setShowAddForm(true)}
                    sx={{ textTransform: "none", fontSize: 12, fontWeight: 600, color: T.gold, "&:hover": { bgcolor: `${T.gold}10` } }}>
                    Assign Vendor
                  </Button>
                )}
              </Box>

              {/* Add form */}
              {showAddForm && (
                <VendorPriceForm
                  itemId={item.id} vendors={vendors} assignedVendorIds={assignedVendorIds}
                  supportedPricingTypes={item.supported_pricing_types ?? []}
                  onSaved={handleVendorSaved} onCancel={() => setShowAddForm(false)}
                />
              )}

              {/* Edit form */}
              {editingPrice && (
                <VendorPriceForm
                  itemId={item.id} vendors={vendors} assignedVendorIds={assignedVendorIds}
                  supportedPricingTypes={item.supported_pricing_types ?? []}
                  editPrice={editingPrice}
                  onSaved={handleVendorSaved} onCancel={() => setEditingPrice(null)}
                />
              )}

              {/* Vendor price rows */}
              {(item.vendor_prices ?? []).length === 0 && !showAddForm ? (
                <Paper elevation={0} sx={{ p: 3, textAlign: "center", borderRadius: "12px", border: `1px dashed ${T.line}` }}>
                  <StorefrontOutlinedIcon sx={{ fontSize: 32, color: T.slateLt, mb: 0.5 }} />
                  <Typography sx={{ fontSize: 12, color: T.slateLt }}>No vendors assigned. Click "Assign Vendor" to add one.</Typography>
                </Paper>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {(item.vendor_prices ?? [])
                    .sort((a: any, b: any) => (b.is_default ? 1 : 0) - (a.is_default ? 1 : 0))
                    .map((vp: any) => (
                      <Paper key={vp.id} elevation={0} sx={{
                        p: 2, borderRadius: "12px",
                        border: vp.is_default ? `1.5px solid ${T.gold}40` : `1px solid ${T.line}`,
                        bgcolor: vp.is_default ? `${T.gold}06` : T.white,
                        "&:hover .vp-actions": { opacity: 1 },
                      }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                          <StorefrontOutlinedIcon sx={{ fontSize: 16, color: vp.is_default ? T.gold : T.slateLt }} />
                          <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.navy, flex: 1 }}>
                            {vp.vendor?.name ?? "Unknown"}
                          </Typography>
                          {vp.is_default && (
                            <Chip icon={<StarIcon sx={{ fontSize: 10, color: `${T.gold} !important` }} />}
                              label="Default" size="small" sx={{ height: 18, fontSize: 9, fontWeight: 700, bgcolor: `${T.gold}15`, color: T.gold }} />
                          )}
                          <Box className="vp-actions" sx={{ opacity: 0, transition: "opacity 0.15s", display: "flex", gap: 0.3 }}>
                            {!vp.is_default && (
                              <Tooltip title="Set as Default">
                                <IconButton size="small" onClick={() => handleSetDefault(vp.id)} sx={{ color: T.slateLt }}>
                                  <StarBorderIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => { setShowAddForm(false); setEditingPrice(vp); }} sx={{ color: T.slateLt }}>
                                <EditOutlinedIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Remove">
                              <IconButton size="small" onClick={() => setDeleteVP({ id: vp.id, vendorName: vp.vendor?.name ?? "Vendor" })} sx={{ color: T.slateLt }}>
                                <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                          <Chip label={PRICING_LABELS[vp.pricing_type] || vp.pricing_type} size="small" sx={{ height: 18, fontSize: 9, bgcolor: `${T.gold}10`, color: T.gold }} />
                          <Tooltip title="Vendor Price">
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.slateLt }}>{fmtCAD(Number(vp.vendor_price))}</Typography>
                          </Tooltip>
                          <Typography sx={{ fontSize: 10, color: T.slateLt }}>&rarr;</Typography>
                          <Tooltip title="DCC Price (selling)">
                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.greenTx }}>{fmtCAD(Number(vp.dcc_price))}</Typography>
                          </Tooltip>
                          {vp.min_hours != null && Number(vp.min_hours) > 0 && (
                            <Chip label={`Min ${vp.min_hours}h`} size="small" sx={{ height: 18, fontSize: 9, bgcolor: T.bg, color: T.slateLt }} />
                          )}
                          {vp.unit_label && (
                            <Chip label={vp.unit_label} size="small" sx={{ height: 18, fontSize: 9, bgcolor: T.bg, color: T.slateLt }} />
                          )}
                        </Box>
                        {vp.image_url && (
                          <Box component="img" src={vp.image_url} alt={`${vp.vendor?.name} image`}
                            sx={{ width: 56, height: 40, objectFit: "cover", borderRadius: "6px", border: `1px solid ${T.line}`, mt: 0.8 }} />
                        )}
                        {vp.vendor?.company_name && (
                          <Typography sx={{ fontSize: 10, color: T.slateLt, mt: 0.8 }}>{vp.vendor.company_name}</Typography>
                        )}
                      </Paper>
                    ))}
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Drawer>

      {/* Delete vendor price confirmation */}
      <Dialog open={!!deleteVP} onClose={() => setDeleteVP(null)} maxWidth="xs" PaperProps={{ sx: { borderRadius: "14px" } }}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 16 }}>
          <WarningAmberIcon color="error" /> Remove vendor?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 13 }}>
            Remove <strong>{deleteVP?.vendorName}</strong> from this item?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteVP(null)} sx={{ textTransform: "none", color: T.slate }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteVP} disabled={deletingVP}
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px" }}>{deletingVP ? "..." : "Remove"}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ItemProfileDrawer;
